// Typed fetch wrappers — the single place that hits the backend. Every call
// carries the Cognito access token; a 401 ends the session.

import { getAccessToken, signOut } from "./auth";
import type {
  AntiRow, AppData, CsvColumnMap, CsvPreview, Deal, DealContext,
  DecisionTerms, FirmConfig, ImportBatchRow, LibraryDoc, Note,
  OutcomeEventRow, PersonDossier, ProcessingState, SectorOverview,
  TeamMember, Theme,
} from "./types";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    await signOut();
    window.location.assign("/");
    throw new Error("Session expired — signing out");
  }
  if (!res.ok) {
    const body: { detail?: string } = await res.json().catch(() => ({}));
    throw new Error(body.detail || `${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

// ── App data + deals ────────────────────────────────────────────────────────

export const fetchAppData = () => request<AppData>("/api/app-data");

export const fetchDeals = () => request<Deal[]>("/api/deals");

/** In-flight + just-completed inbound emails (the Inbox "N processing" chip). */
export const fetchProcessing = () => request<ProcessingState>("/api/processing");

export const fetchDeal = (id: string) => request<Deal>(`/api/deals/${id}`);

export const patchDeal = (id: string, changes: Partial<Deal>) =>
  request<Deal>(`/api/deals/${id}`, json("PATCH", changes));

/** Correct a bad extraction: edit field values and/or tags (re-scores). */
export const editDealFields = (id: string, payload: { fields?: Deal["fields"]; tags?: string[] }) =>
  request<Deal>(`/api/deals/${id}/fields`, json("PATCH", payload));

/** The "Prior work" panel: precedents, same-sector history, firm knowledge. */
export const getDealContext = (id: string, refresh = false) =>
  request<DealContext>(`/api/deals/${id}/context${refresh ? "?refresh=1" : ""}`);

export const dismissPrecedent = (id: string, slug: string) =>
  request<{ ok: boolean }>(`/api/deals/${id}/precedents/${slug}`, { method: "DELETE" });

export const getPerson = (entityId: string) =>
  request<PersonDossier>(`/api/people/${entityId}`);

export interface DealEmail {
  id: string;
  subject: string;
  sender: string;
  sentAt: string;
  direction: "in" | "out";
  body: string;
}

/** Correspondence timeline — durable, survives round-merges. */
export const getDealEmails = (id: string) =>
  request<DealEmail[]>(`/api/deals/${id}/emails`);

/** Cross-deal document library; q searches names, companies AND content. */
export const fetchDocuments = (q = "") =>
  request<LibraryDoc[]>(`/api/documents${q ? `?q=${encodeURIComponent(q)}` : ""}`);

export interface MergeSuggestion {
  id: string;
  kind: "person" | "company" | "sector";
  reason: string;
  primary: { id: string; name: string; detail: string };
  duplicate: { id: string; name: string; detail: string };
}

/** Graph review queue: suspected duplicate entities awaiting a human call. */
export const getMergeSuggestions = () =>
  request<MergeSuggestion[]>("/api/graph/merges");

export const decideMerge = (id: string, action: "merge" | "reject" | "undo") =>
  request<{ ok: boolean; status: string }>(`/api/graph/merges/${id}`, json("POST", { action }));

export interface DecisionPayload {
  kind: "pass" | "invest" | "review";
  reasonTags?: string[];
  reason?: string;
  conviction?: number;
  terms?: DecisionTerms;
}

export const recordDecision = (id: string, payload: DecisionPayload) =>
  request<Deal>(`/api/deals/${id}/decision`, json("POST", payload));

export const addNote = (id: string, body: string) =>
  request<Note>(`/api/deals/${id}/notes`, json("POST", { body }));

export async function processUpload(
  emailText: string,
  files: File[],
  routeId?: string,
): Promise<Deal> {
  const fd = new FormData();
  fd.append("email_text", emailText);
  if (routeId) fd.append("route_id", routeId);
  files.forEach((f) => fd.append("files", f));
  return request<Deal>("/api/process", { method: "POST", body: fd });
}

export async function deleteDeal(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/deals/${id}`, { method: "DELETE" });
}

/** Authenticated document download — plain <a href> can't carry the bearer
 *  token, so fetch the bytes and hand the browser a blob URL. */
export async function downloadDocument(dealId: string, name: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(
    `/api/deals/${dealId}/documents/${encodeURIComponent(name)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Attach memos/decks to an existing deal; text becomes queryable in Knowledge. */
export async function attachDocuments(dealId: string, files: File[]): Promise<Deal> {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return request<Deal>(`/api/deals/${dealId}/documents`, { method: "POST", body: fd });
}

/** Attach a URL to a deal. Publicly-shared Google Docs/Sheets/Slides also get
 *  their text pulled and indexed into Knowledge. */
export const addDealLink = (dealId: string, url: string, title?: string) =>
  request<Deal>(`/api/deals/${dealId}/links`, json("POST", { url, title }));

export const deleteDealLink = (dealId: string, linkId: string) =>
  request<Deal>(`/api/deals/${dealId}/links/${linkId}`, { method: "DELETE" });

// ── Anti-portfolio ──────────────────────────────────────────────────────────

export const addAnti = (payload: {
  dealId?: string; co?: string; reasonThen?: string; then?: string; mult?: string;
}) =>
  request<AntiRow>("/api/anti", json("POST", payload));

export const patchAnti = (id: string, changes: Partial<AntiRow>) =>
  request<AntiRow>(`/api/anti/${id}`, json("PATCH", changes));

export const deleteAnti = (id: string) =>
  request<{ ok: boolean }>(`/api/anti/${id}`, { method: "DELETE" });

// ── Firm-memory imports (KG-4/5) ───────────────────────────────────────────

export async function previewCsvImport(file: File): Promise<CsvPreview> {
  const fd = new FormData();
  fd.append("file", file);
  return request<CsvPreview>("/api/import/csv/preview", { method: "POST", body: fd });
}

export async function commitCsvImport(
  file: File,
  mapping: CsvColumnMap[],
  defaultStatus: string,
): Promise<{ batchId: string; created: number; slugs: string[]; skipped: { row: number; co?: string; reason: string }[] }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mapping", JSON.stringify(mapping));
  fd.append("defaultStatus", defaultStatus);
  return request("/api/import/csv", { method: "POST", body: fd });
}

export async function importDocuments(files: File[]): Promise<{
  batchId: string;
  attached: { slug: string; name: string; indexed: boolean }[];
  unmatched: string[];
  /** Files skipped because the deal already has a same-named document. */
  conflicts: { slug: string; name: string }[];
}> {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return request("/api/import/documents", { method: "POST", body: fd });
}

export async function importMailbox(file: File): Promise<{
  batchId: string;
  emails: number;
  duplicates: number;
  unmatched: number;
  matched: Record<string, number>;
  referrers: { slug: string; name: string; email: string }[];
}> {
  const fd = new FormData();
  fd.append("file", file);
  return request("/api/import/mailbox", { method: "POST", body: fd });
}

export const fetchImportBatches = () =>
  request<ImportBatchRow[]>("/api/import/batches");

export const undoImportBatch = (id: string) =>
  request<{ removed: string[]; kept: string[]; status: string }>(
    `/api/import/batches/${id}/undo`, { method: "POST" });

// ── Graph sector view + outcome loop ───────────────────────────────────────

export const fetchGraphSectors = () =>
  request<SectorOverview[]>("/api/graph/sectors");

export const fetchOutcomes = (deal?: string) =>
  request<OutcomeEventRow[]>(`/api/outcomes${deal ? `?deal=${encodeURIComponent(deal)}` : ""}`);

export const recordOutcome = (payload: {
  dealSlug?: string; companyName?: string; event: string; date?: string;
  valueUsd?: number | string | null; acquirer?: string; note?: string;
}) =>
  request<OutcomeEventRow>("/api/outcomes", json("POST", payload));

export const deleteOutcome = (id: string) =>
  request<{ ok: boolean }>(`/api/outcomes/${id}`, { method: "DELETE" });

// ── Firm config + team ──────────────────────────────────────────────────────

export const saveFirmConfig = (config: FirmConfig) =>
  request<FirmConfig>("/api/firm-config", json("PUT", config));

export const inviteMember = (payload: { email: string; name: string; role: string }) =>
  request<TeamMember>("/api/team/invites", json("POST", payload));

export const patchMember = (id: string, changes: { role?: string; status?: string }) =>
  request<TeamMember>(`/api/team/${id}`, json("PATCH", changes));

export const reassignMemberDeals = (id: string, to: string) =>
  request<{ moved: number; to: string }>(`/api/team/${id}/reassign`, json("POST", { to }));

export const resendInvite = (id: string) =>
  request<{ ok: boolean; email: string }>(`/api/team/${id}/resend`, { method: "POST" });

// ── Knowledge ───────────────────────────────────────────────────────────────

export const fetchKnowledgeChats = <T>() => request<T[]>("/api/knowledge/chats");

export const askKnowledge = <T>(question: string, threadId?: string) =>
  request<T>("/api/knowledge/ask", json("POST", { question, threadId }));

export const patchKnowledgeChat = <T>(id: string, saved: boolean) =>
  request<T>(`/api/knowledge/chats/${id}`, json("PATCH", { saved }));

/** Per-user Helpful/Off-base rating on an answer; null clears it. */
export const setChatFeedback = (id: string, rating: "up" | "down" | null) =>
  request<{ ok: boolean; rating: "up" | "down" | null }>(
    `/api/knowledge/chats/${id}/feedback`, json("POST", { rating }));

// ── Investment themes ─────────────────────────────────────────────────────────

export const fetchThemes = () => request<Theme[]>("/api/knowledge/themes");

/** Generate draft themes from the firm's deals. Optional `guidance` tailors
 *  them (e.g. a sector or stage focus). */
export const generateThemes = (guidance?: string) =>
  request<Theme[]>(
    "/api/knowledge/themes/generate",
    json("POST", guidance?.trim() ? { guidance: guidance.trim() } : {}),
  );

export const createTheme = (t: Partial<Theme>) =>
  request<Theme>("/api/knowledge/themes", json("POST", t));

/** AI-assisted: a topic → an editable, grounded draft theme (not yet saved). */
export const draftTheme = (prompt: string) =>
  request<Theme>("/api/knowledge/themes/draft", json("POST", { prompt }));

export const patchTheme = (id: string, changes: Partial<Theme>) =>
  request<Theme>(`/api/knowledge/themes/${id}`, json("PATCH", changes));

export const deleteTheme = (id: string) =>
  request<{ ok: boolean }>(`/api/knowledge/themes/${id}`, { method: "DELETE" });
